import { notify } from "@kyvg/vue3-notification";
import { VTextField } from "vuetify/lib/components/index.mjs";
import { boolean, mixed, number, setLocale, string } from 'yup';
import { $dy__fetch, extractDataOrFailure, useDYFetch, useDYFetchWithToken } from "./fetch";
import { hasOwnProperty } from "../libs/utils";
import { isBoolean, isInteger, isNumber, isString } from "lodash-es";
import { useDYAppConfig } from "./config";
import { parsePhoneNumber, isValidPhoneNumber, type CountryCode } from 'libphonenumber-js';

setLocale({
  mixed: {
    default: '설정된 값이 유효하지 않습니다',
    required: '해당 값은 필수값입니다',
    notType: '유효하지 않은 타입입니다'
  },
  number: {
    min(params) {
      return `최소 ${params.min} 이상이어야 합니다`
    },
    max(params) {
      return `최대 ${params.max} 을 넘을수는 없습니다`
    },
    lessThan(params) {
      return `최대 ${params.less} 미만이어야만 합니다`
    },
    moreThan(params) {
      return `최소 ${params.more} 초과해야만 합니다`
    },
  },
});

/**
 * form자체는 FormContext타입이다.
 * 
 const handleSubmit = async (event: SubmitEventPromise) => {
  const results = await event

  if (results.valid) {
    isProcessing.value = true
    const submitData = cloneDeep(formParams.value)
    await insert(submitData)
    params.value.page = 1
    await fetch()
    isProcessing.value = false

    formParams.value.memos = ''
    emit('submit:memos', submitData)
  }
}
 * @returns 
 */
export function useValidateRules() {
  const { API_BASE_URL } = useDYAppConfig()

  const checkboxRequiredRule = (value: any) => {
    if (value) {
      return true;
    }

    return '반드시 체크해야만 합니다';
  }

  /**
   * 중복여부도 체크함
   */
  const emailRule = string()
    .required('필수로 입력해야 합니다.')
    .email('올바른 형태의 이메일 주소를 입력해주세요. 예) example@example.com')
    .test({
      test: async (value, ctx) => {
        // 2026-10-18 빈 값·형식 오류는 앞의 required·email 규칙이 이미 잡으므로 중복확인 API 를 부르지 않는다
        //   (vee-validate 가 abortEarly:false 로 모든 규칙을 돌려, 빈 값에도 매번 API 를 불렀다)
        if (!value || !string().email().isValidSync(value)) return true
        try {
          const data = await $dy__fetch<any>(`${API_BASE_URL}/api/members/check-duplicated-id`, { method: 'post', body: { id: value } })
          const result = data === 'true' || data === true ? true : ctx.createError({ message: data.message ?? '중복되는 아이디입니다. 다른 아이디를 입력하세요.' })
          return result
        } catch (e: any) {
          return ctx.createError({ message: '서버 오류! 잠시 후 다시 시도해 주세요.' })
        }
      }
    })

  /**
   * api로 중복 이메일 검사를 하는게 아닌 순수한 email형태인지까지만 체크
   */
  const emailRuleNoApi = string()
    .required('필수값입니다.')
    .email('올바른 형태의 이메일 주소를 입력해주세요. 예) example@example.com')

  /**
   * 서버 검증없이 전화번호 패턴 검사만 하는 규칙. 지역번호 괄호는 안되나, 우리가 쓰는 포맷팅 라이브러리와 호환이 가장 좋은듯
   * @deprecated 사용하지 않음. 대신 phoneRule 사용
   */
  const phoneRuleBasic = string().required('필수값입니다.').matches(/^\+?\d{1,4}?[-.\s]?\(?\d{1,3}?\)?[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/, '올바른 전화번호 패턴이 아닙니다.')

  interface formattedNumbers {
    E164: string
    INTERNATIONAL: string
    NATIONAL: string
    RFC3966: string
  }

  /**
   * @param countryCode 
   * @param callBack 
   * 
   * formattedNumber는 일반적인 국내용 패턴
   * 
   * formattedNumbers 객체예제
   * E164 : "+817043128014"
   * INTERNATIONAL : "+81 70-4312-8014"
   * NATIONAL : "070-4312-8014"
   * RFC3966 : "tel:+81-70-4312-8014"
   * @returns 
   */
  // 2026-05-06 애플 인앱 심사 정책상 휴대전화번호 필수 수집이 금지되어 옵션 처리 가능하도록 required 인자 추가
  const phoneRule = (countryCode: string, callBack?: (formattedNumber: string, formattedNumbers: formattedNumbers) => {}, required: boolean = true) => {
    const baseSchema = required ? string().required('필수값입니다.') : string()
    return baseSchema.test({
      test: (value, ctx) => {
        try {
          if (!value) {
            // 2026-05-06 옵셔널인 경우 빈 값은 통과시켜 휴대전화번호 미입력을 허용
            if (!required) return true
            return ctx.createError({ message: '전화번호를 입력해주세요.' })
          }

          const parsed = parsePhoneNumber(value, countryCode as CountryCode)

          if (!parsed || !parsed.isValid()) {
            return ctx.createError({ message: '유효하지 않은 전화번호입니다.' })
          }

          if (callBack) {
            const nums: formattedNumbers = {
              E164: parsed.format('E.164'),
              INTERNATIONAL: parsed.formatInternational(),
              NATIONAL: parsed.formatNational(),
              RFC3966: parsed.format('RFC3966'),
            }
            callBack(parsed.formatNational(), nums)
          }
        } catch (e: any) {
          return ctx.createError({ message: '유효하지 않은 전화번호입니다.' })
        }

        return true
      }
    })
  }

  const pwdRule = string()
    .required()
    .min(8, '최소 8자 이상 입력해야 합니다.')
    .max(16, '최대 16자까지만 입력할 수 있습니다.')
    .test({
      test: (value, ctx) => {
        if (!value) {
          return false
        }

        const reg1 = /[0-9]/g
        const reg2 = /[a-z]/ig
        const reg3 = /[~!@@#$%^&*|\\\'\";:\/?]/gi

        if ((!reg1.test(value) && !reg2.test(value)) || (!reg2.test(value) && !reg3.test(value)) || (!reg3.test(value) && !reg1.test(value))) {
          return ctx.createError({ message: '영문, 숫자, 특수문자 중 2가지 이상을 혼합하여 입력해주세요.' })
        }

        if (/(\w)\1\1\1/.test(value)) {
          return ctx.createError({ message: '같은 문자를 4번 이상 사용하실 수 없습니다.' })
        }

        return true
      }
    })

  // const confirmPwdRule = (origin?:Ref<string>) => {
  //   string().required().oneOf([origin.value], '비밀번호가 일치해야 합니다')
  // }

  /**
    * 여기서 value값은 사용하는 곳에서 computed로 쓰면 됨 ex) computed(()=>value)
    * 리턴값 조절해야함
    */
  const makeCheckboxRequireRule = (value: Ref<boolean>) => {
    // if (value) {
    //   return true;
    // }

    // return '반드시 체크해야만 합니다';
  }

  const makeNicknameRule = (modify: boolean) => {
    const { API_BASE_URL } = useDYAppConfig()

    return string().test({
      test: async (value, ctx) => {
        if (!value) return true //닉네임 자체를 입력안할수도 있으므로

        const response = await useDYFetchWithToken<any>(  API_BASE_URL+'/api/members/check-duplicated-nickname', { method: 'post', body: { nickname: value, modify }, immediate: false })
        await response.execute({dedupe: 'defer'})
        
        if (response.data.value !== 'true') return ctx.createError({ message: '중복되는 닉네임입니다. 다른 닉네임을 입력하세요' })

        return true
      }
    })
  }

  /**
   * 여기서 pwdValue값은 사용하는 곳에서 computed로 쓰면 됨 ex) computed(()=>formData.value.pwd)
   */
  const makePwdConfirmRule = (pwdValue: Ref<string>) => {
    return string()
      .required('필수로 입력해야 합니다.')
      .test('pwdConfirm', '비밀번호가 일치하지 않습니다.', (value) => {
        return value === pwdValue.value
      })
  }

  const booleanRequireRule = boolean().required('필수값입니다')
  const mixedRequireRule = mixed().required('필수값입니다')
  const numRequireRule = mixed().test('numRequireRule', (val) => ` 필수값입니다`,
    (val) => !!val
  ).test('numRequireRule', (val) => ` 반드시 숫자여야만 합니다`,
    (val) => !isNaN(Number(val))
  )

  const onlyStringRule = string().test({
    test: (value, ctx) => {
      if (value !== undefined) {
        if (/\d/.test(value)) return ctx.createError({ message: `숫자는 입력할 수 없습니다.` })
        return true
      }
    }
  }).required('필수값입니다')

  const strRequireRule = string().required('필수값입니다')

  const numericMinRule = (minValue: Ref<number>, message?: string) => {
    return number().test({
      test: (value, ctx) => {
        if (value !== undefined) {
          if (minValue.value < value) return ctx.createError({ message: message ?? `최소 ${minValue.value} 이상이어야 합니다` })

          return true
        }
      }
    })
  }

  const numericMaxRule = (maxValue: Ref<number>, message?: string) => {
    number().test({
      test: (value, ctx) => {
        if (value !== undefined) { //0도 체크하기 위함
          if (maxValue.value < value) return ctx.createError({ message: message ?? `최대 ${maxValue.value} 을 넘을수는 없습니다` })

          return true
        }
      }
    })
  }

  /**
   * 파라미터가 필요한 경우 바로 필드에 가져다 쓰면 안되고, 이미 선언한 상태로 가져와야만 한다
   * 또한 동적으로 변하는 파라미터를 가져다 쓸때는 yup기본 정의된 함수를 쓰면 안되고, 반드시 test로 커스텀 구현을 해야만 한다.
   * 이때 파라미터는 Ref값으로 받도록 해서 사용하는 곳에서는 computed로 보내야만 한다.
   * 
   * 잘못된 예시 : <Field :rules="minMaxRule(params)" />
   * 
   * 올바른 예시 : 
   * const valRule = {
      minMaxRule: numericMinMaxRule(minValue, computed(()=>props.point))
     }
      <Field :rules=" valRule.minMaxRule" />
   */
  const numericMinMaxRule = (minValue: Ref<number>, maxValue: Ref<number>, minMessage?: string, maxMessage?: string) => {
    return number().test({
      test: (value, ctx) => {
        if (value !== undefined) {
          if (minValue.value > value) return ctx.createError({ message: minMessage ?? `최소 ${minValue.value} 이상이어야 합니다` })
          if (maxValue.value < value) return ctx.createError({ message: maxMessage ?? `최대 ${maxValue.value} 을 넘을수는 없습니다` })

          return true
        }
      }
    })
  }

  return {
    checkboxRequiredRule, emailRule, emailRuleNoApi, phoneRuleBasic, phoneRule, pwdRule, numRequireRule, onlyStringRule, strRequireRule, makeCheckboxRequireRule, makeNicknameRule, makePwdConfirmRule, mixedRequireRule, booleanRequireRule, numericMaxRule, numericMinMaxRule
  }
}

function _transLanguage(field: string) {
  switch (field) {
    case 'accountNumber': return { ko: "계좌번호는" }
    case 'address': return { ko: "주소는" }
    case 'agree': return { ko: "동의는" }
    case 'bankName': return { ko: "은행명은" }
    case 'bank-owner': return { ko: "예금주는" }
    case 'birthday': return { ko: "생년월일은" }
    case 'contents': return { ko: "내용은" }
    case 'desiredPart': return { ko: "희망 파트는" }
    case 'email': return { ko: "이메일은" }
    case 'feature-prons': return { ko: "특징/장점은" }
    case 'id': return { ko: "아이디는" }
    case 'inquiry': return { ko: "문의사항은" }
    case 'modifyTitle': return { ko: "수정시 내용은" }
    case 'name': return { ko: "이름은" }
    case 'phone': return { ko: "연락처는" }
    case 'pwd': return { ko: "비밀번호는" }
    case 'pwdConfirm': return { ko: "비밀번호 확인은" }
    case 'recommend-target': return { ko: "추천대상은" }
    case 'refund-reason': return { ko: "환불사유는" }
    case 'replyTitle': return { ko: "댓글시 내용은" }
    case 'subject': return { ko: "제목은" }
    case 'tel': return { ko: "휴대번호는" }
    case 'title': return { ko: "제목은" }
  }
}

export function useValidate() {

  /**
 * 슬롯 안의 있는 인풋요소들은 자동으로 밸리데이팅이 되지 않기에 알림만 띄울 수 있을 뿐 강조효과나 에러효과가 들어가지 않는다. 사용자 경험상 이런경우 문제있는 필드에 강조효과를 주기 위한 처리
 */
  const slotInputError = (form: Ref, id: string, message: string) => {
    const html = form.value?.querySelector('#' + id)
    if (html instanceof HTMLElement) {
      html.querySelector('input')?.focus()
      html.querySelector('div')?.classList?.add('animate-bounce')
    }

    notify({ type: 'warn', text: message })
    // isProcessing.value = false
    return false
  }

  /**
   * 밸리데이션 v-slot의 filed와 errors를 그대로 넘길것
   * @param field 
   * @param erros 
   */
  function transErrorMessage(field: string, error: string) {
    if (!error) return null

    let lang = 'ko'
    const selectLanguagePack = _transLanguage(field)

    if (selectLanguagePack && hasOwnProperty(selectLanguagePack, lang)) {
      return selectLanguagePack[lang] + ' ' + error
    } else {
      return error
    }
  }

  function transErrorMessageVeeValidate(field: any, errors: any) {
    if (errors.length < 1) return undefined
    return transErrorMessage(field.name, errors[0])
  }

  /**
  * vee-validate 검증을 사용했을 때, 자동으로 실패한 검증건을 알림 띄워주고 해당 요소에 포커싱 해주는 메서드
  * @param result // 폼에서 invalid-submit 이벤트에 할당한 메서드의 파라미터를 그대로 넘기거나, await agreeForm.value?.validate() 로 수동으로 검증호출한 결과물을 넘길것
   */
  const validateNotify = (result: { errors: any }, koreanNoti?: { name: string, ko: string }[]) => {
    let lang = 'ko'

    if (result.errors) {
      let notified = false

      if (koreanNoti) {
        koreanNoti.forEach((noti) => {
          if (Object.keys(result.errors)[0] === noti.name) {
            notify({ type: 'error', text: noti.ko + ' ' + result.errors[Object.keys(result.errors)[0]] })
            notified = true
          }
        })
      }

      if (notified) return

      let selectLanguagePack = _transLanguage(Object.keys(result.errors)[0])

      notify({
        type: 'error', text: selectLanguagePack && hasOwnProperty(selectLanguagePack, lang)
          ? selectLanguagePack[lang] + ' ' + result.errors[Object.keys(result.errors)[0]]
          : result.errors[Object.keys(result.errors)[0]]
      })

      //검증실패 요소에 포커싱 해주기 위한 요소. 각 키와 일치한 id설정은 필요함..
      document.getElementById(Object.keys(result.errors)[0])?.focus()
    }
  }

  const validateNotifyByElm = (result: { errors: any }, elements: { name: string, element?: HTMLElement | VTextField }[]) => {
    let firstFail = false
    elements.forEach((item) => {
      //실제 에러인 경우만 노티파이로 알림
      if (result.errors[item.name]) {
        notify({ type: 'error', text: `${item.name} 항목은 ${result.errors[item.name]}` })

        if (!firstFail && item.element) {
          item.element.focus()
          firstFail = true
        }
      }
    });
  }

  return {
    validateNotifyByElm, transErrorMessage, transErrorMessageVeeValidate, validateNotify, slotInputError
  }
}

/**
 * 뷰티파이용 v-from기반 검증 룰들
 * @returns 
 */
export function useVFormValidate() {
  /**
   * 숫자의 크기를 비교하고 싶은 경우꼭 넘길때 Number로 밸류를 감쌀 것!!
   * :rules="[(value) => max(value, 10)]" 이런 형태로 사용
   * @param value 
   * @param maxValue 
   * @param message 
   * @returns 
   */
  const max = (value: string | number, maxValue: number, message?: string) => {
    const errMessage = message ? message : `최대값은 ${maxValue} 입니다`
    const condition = typeof (value) === 'string' ? value.length <= maxValue : value <= maxValue
    return condition ? true : errMessage
  }

  /**
   * 숫자의 크기를 비교하고 싶은 경우꼭 넘길때 Number로 밸류를 감쌀 것!!
   * @param value 
   * @param minValue 
   * @param message 
   * @returns 
   */
  const min = (value: string | number, minValue: number, message?: string) => {
    const errMessage = message ? message : `최소값은 ${minValue} 입니다`
    const condition = typeof (value) === 'string' ? value.length >= minValue : value >= minValue
    return condition ? true : errMessage
  }

  const required = (value: any, message: string = '필수 입력값입니다.') => {
    if (isBoolean(value)) {
      return true
    }
    return (isNumber(value) && value >= 0) || value ? true : message
  }

  /**
   * 특정 조건하에서만 required 검증을 해주고 싶은 경우 사용
   * @param value 
   * @param condition 
   * @param message 
   * @returns 
   */
  const requiredCondition = (value: any, condition: boolean, message: string = '필수 입력값입니다.') => {
    return condition ? required(value, message) : true
  }

  const number = (value: any, message: string = '숫자만 입력 가능합니다.') => {
    console.log('value', Number(value))
    return Number(value) ? true : message
  }

  const phoneRuleBasic = (value: any, message: string = '올바른 전화번호 패턴이 아닙니다.') => {
    return isString(value) && (/^\+?\d{1,4}?[-.\s]?\(?\d{1,3}?\)?[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/.test(String(value))) ? true : message
  }

  return {
    max, min, required, number, requiredCondition, phoneRuleBasic
  }
}